import { FfsRegionClassification } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsRegionClassificationSchema'
import { componentTypeEligibility } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsRegionRouting'
import { FfsResultType } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsTypes'

import { isPartLevelEligible } from './ffsLevels'
import { pressuresDisagree, toCanonicalPressure } from './ffsPressure'
import { FfsReportRecord } from './ffsReportRecord'
import { ScenarioKey } from './ffsScenarioKey'

/**
 * Which saved report GOVERNS an LTA's verdict at close-out.
 *
 * BEST-OF across every API-579 type-eligible (part, level) the engineer actually
 * ran, in one unit-wide scenario: the least-severe verdict wins (`Pass > Derate >
 * Fail`), so **any Pass at any eligible cell makes the LTA a Pass**. This is the
 * SME rule — a permitted alternative assessment is first-class, and the engineer
 * is not forced onto `entryRoute`'s part. `entryRoute` is therefore only the
 * *recommended* cell (display) + an availability input; it no longer selects the
 * verdict, and the old within-part L1→L2 escalation ladder is gone (a Fail is
 * terminal).
 *
 * The engineer's `is_default` flag is NOT the selector (it is user-controlled and
 * drives the reporting query); it is read only to report divergence, scoped to
 * the in-scenario candidates.
 */

/** How far a report's calc timestamp may trail the classification before stale. */
export const STALENESS_TOLERANCE_MS = 5 * 60_000

/** The collapsed state of ONE cell (a single part+level) after duplicates resolve. */
export type LevelState =
  | { kind: 'absent' }
  | { kind: 'pending' }
  | { kind: 'ambiguous' }
  | { kind: 'report'; report: FfsReportRecord }

export type UnassessedReason =
  | 'no-report'
  | 'not-in-scenario'
  | 'no-usable-scenario'
  | 'pending'
  | 'ambiguous'
  | 'stale'
  /** In scenario + eligible, but saved before `computedAt` existed → re-save. */
  | 'needs-resave'
  /** Reports exist in scenario but only at (part, level) cells the component
   * type is not eligible for — re-run at an eligible level. */
  | 'ineligible-level'
  | 'unparseable'

export type GoverningSelection =
  | {
      kind: 'selected'
      report: FfsReportRecord
      /** True when the engineer's flag points at a different report. Display only. */
      disagreesWithDefault: boolean
      /** The classification timestamp was unusable, so staleness could not be checked. */
      timestampUnusable: boolean
    }
  | { kind: 'unassessed'; reason: UnassessedReason }

/**
 * Collapse one cell's candidates into a single state.
 *
 * Report ids are freshly generated on every save and saving never replaces
 * (`FfsReportPanel.tsx`), so genuine duplicates at one part+level are possible.
 */
export function normaliseLevel(
  candidates: ReadonlyArray<FfsReportRecord>
): LevelState {
  if (candidates.length === 0) return { kind: 'absent' }

  // `pending` means "not yet answered", so it neither wins nor conflicts.
  const answered = candidates.filter((r) => r.result !== FfsResultType.Pending)
  if (answered.length === 0) return { kind: 'pending' }
  if (answered.length === 1) return { kind: 'report', report: answered[0] }

  const [first, ...rest] = answered
  const conflicts = rest.some(
    (r) =>
      r.result !== first.result ||
      // MAWP_r is the acting quantity ONLY for a Derate; comparing it for
      // Pass/Fail/Skipped would flag agreeing verdicts as ambiguous over an
      // irrelevant field — which would violate any-Pass semantics.
      (first.result === FfsResultType.Derate &&
        pressuresDisagree(r.mawpr, first.mawpr))
  )
  if (conflicts) return { kind: 'ambiguous' }

  // They agree; pick deterministically by (updatedAt, id). NOT by `generation`
  // — that counts updates to a single entry, so it cannot order across entries.
  const newest = answered.reduce((best, r) => {
    if (r.updatedAt > best.updatedAt) return r
    if (r.updatedAt < best.updatedAt) return best
    return r.id > best.id ? r : best
  }, first)
  return { kind: 'report', report: newest }
}

/**
 * Why Step A produced no candidates. Evaluated against the LTA's UNFILTERED
 * reports so the engineer is told what to fix, not just that nothing matched.
 */
function emptyCandidateReason(
  reports: ReadonlyArray<FfsReportRecord>,
  scenarioKey: ScenarioKey | null,
  eligibility: ReturnType<typeof componentTypeEligibility>
): UnassessedReason {
  if (reports.length === 0) return 'no-report'
  const keyed = reports.filter((r) => r.scenarioKey !== null)
  if (keyed.length === 0) return 'no-usable-scenario'
  if (scenarioKey === null) return 'not-in-scenario'
  const inScenario = keyed.filter((r) => r.scenarioKey === scenarioKey)
  if (inScenario.length === 0) return 'not-in-scenario'
  // Reports exist in this scenario but ALL sit at (part, level) cells this
  // component type is not eligible for — tell the engineer to re-run at an
  // eligible level, not that nothing was saved.
  if (
    !inScenario.some((r) => isPartLevelEligible(eligibility, r.part, r.level))
  ) {
    return 'ineligible-level'
  }
  return 'no-report'
}

/** The derate whose re-rate MAWP is highest in canonical units. A derate with an
 * unusable MAWP ranks below any usable one and is chosen only when it is the sole
 * derate (its pressure then propagates as `unavailable`). */
function highestRerate(
  derates: ReadonlyArray<FfsReportRecord>
): FfsReportRecord {
  return derates.reduce((best, r) => {
    const rp = toCanonicalPressure(r.mawpr)
    const bp = toCanonicalPressure(best.mawpr)
    if (rp === null) return best
    if (bp === null) return r
    return rp > bp ? r : best
  })
}

export interface SelectGoverningReportArgs {
  /** Must NOT be Type C — the caller branches that before reaching here. */
  classification: FfsRegionClassification
  /** Every report tagged to this LTA, unfiltered. */
  reports: ReadonlyArray<FfsReportRecord>
  /** The unit-wide scenario being signed off; `null` when none could be derived. */
  scenarioKey: ScenarioKey | null
  /** Reports tagged to this LTA that could not be parsed at all. */
  parseFailures: number
}

export function selectGoverningReport({
  classification,
  reports,
  scenarioKey,
  parseFailures,
}: SelectGoverningReportArgs): GoverningSelection {
  // An unreadable sibling could BE the governing report, so its LTA cannot be
  // resolved — dropping it silently might leave a stale Pass looking governing.
  if (parseFailures > 0) return { kind: 'unassessed', reason: 'unparseable' }

  const eligibility = componentTypeEligibility(classification.componentType)

  // --- Step A: candidates = in the target scenario AND at a type-eligible cell.
  // The routed part is NOT special: any API-579-eligible (part, level) result is
  // a first-class alternative, so `entryRoute` does not filter here.
  const inScenario = reports.filter(
    (r) =>
      scenarioKey !== null &&
      r.scenarioKey === scenarioKey &&
      isPartLevelEligible(eligibility, r.part, r.level)
  )
  if (inScenario.length === 0) {
    return {
      kind: 'unassessed',
      reason: emptyCandidateReason(reports, scenarioKey, eligibility),
    }
  }

  // --- Step A2: comparability + staleness via the IMMUTABLE `computedAt`. A
  // candidate must have been computed at/after the current classification
  // decision. A legacy row with no `computedAt` is fail-closed (`needs-resave`)
  // rather than trusted via the mutable `updatedAt`, which `is_default` bumps.
  const decidedMs = Date.parse(classification.decidedAt)
  const decidedUnusable = Number.isNaN(decidedMs)
  const fresh: FfsReportRecord[] = []
  let sawStale = false
  let sawMissingTimestamp = false
  for (const r of inScenario) {
    // The immutable-timestamp requirement is ALWAYS enforced: a legacy row with
    // no `computedAt` is fail-closed (`needs-resave`), even when the
    // classification timestamp itself is unreadable — otherwise a bad `decidedAt`
    // would defeat both the staleness gate AND the immutable-timestamp guarantee.
    const computedMs = r.computedAt ? Date.parse(r.computedAt) : NaN
    if (Number.isNaN(computedMs)) {
      sawMissingTimestamp = true
      continue
    }
    if (decidedUnusable) {
      // The report carries a valid immutable stamp, but the classification's
      // timestamp can't be compared to it — trust the report and surface
      // `timestampUnusable` rather than stranding every legacy classification.
      fresh.push(r)
      continue
    }
    if (computedMs < decidedMs - STALENESS_TOLERANCE_MS) {
      sawStale = true
      continue
    }
    fresh.push(r)
  }
  if (fresh.length === 0) {
    return {
      kind: 'unassessed',
      reason: sawMissingTimestamp
        ? 'needs-resave'
        : sawStale
          ? 'stale'
          : 'no-report',
    }
  }

  // --- Step B: collapse duplicates within each (part, level) cell.
  const byCell = new Map<string, FfsReportRecord[]>()
  for (const r of fresh) {
    const key = `${r.part}:${r.level}`
    const bucket = byCell.get(key)
    if (bucket) bucket.push(r)
    else byCell.set(key, [r])
  }
  const cells = Array.from(byCell.values(), normaliseLevel)
  const clean = cells.flatMap((c) => (c.kind === 'report' ? [c.report] : []))

  const timestampUnusable = decidedUnusable
  const chosen = (report: FfsReportRecord): GoverningSelection => {
    // Divergence is display-only. Scoped to IN-SCENARIO reports (not just
    // `fresh`): a default flag on an out-of-scenario report can't raise a
    // spurious badge, but a flag on an in-scenario report that is stale/excluded
    // SHOULD surface — that's exactly the case where the engineer's chosen
    // default is not the report that actually governs.
    const flagged = inScenario.find((r) => r.isDefault)
    return {
      kind: 'selected',
      report,
      disagreesWithDefault: Boolean(flagged && flagged.id !== report.id),
      timestampUnusable,
    }
  }

  // --- Step C: best-of (min severity). A clean Pass dominates everything,
  // including an ambiguous sibling cell — nothing beats a Pass, so ambiguity is
  // moot once one exists.
  const pass = clean.find((r) => r.result === FfsResultType.Pass)
  if (pass) return chosen(pass)

  // With no clean Pass, an ambiguous cell could be concealing one — block.
  if (cells.some((c) => c.kind === 'ambiguous')) {
    return { kind: 'unassessed', reason: 'ambiguous' }
  }

  // Derate beats Fail; among derates the highest usable re-rate governs (the
  // least-conservative acceptable pressure). The component rollup then takes the
  // LOWEST across LTAs — min-within, max-across stays conservative.
  const derates = clean.filter((r) => r.result === FfsResultType.Derate)
  if (derates.length > 0) return chosen(highestRerate(derates))

  const fail = clean.find((r) => r.result === FfsResultType.Fail)
  if (fail) return chosen(fail)

  // No verdict at all: a Skipped resolves the LTA (non-blocking); else pending.
  const skipped = clean.find((r) => r.result === FfsResultType.Skipped)
  if (skipped) return chosen(skipped)
  if (cells.some((c) => c.kind === 'pending')) {
    return { kind: 'unassessed', reason: 'pending' }
  }
  return { kind: 'unassessed', reason: 'no-report' }
}
